/**
 * SASADM-0154 — live theme preview: an app shell rendered in light and dark,
 * side by side (stacked on narrow screens). Every colour, radius and font comes
 * from the server's `resolved` document — no client-side colour maths.
 */
import { Box, Grid, Typography } from '@mui/material';
import { memo } from 'react';
import type { ResolvedTheme, ResolvedThemeFont, ThemeMode } from '@/api/types';
import { useCopy } from '@/hooks/useCopy';

const STATUSES = ['success', 'warning', 'error', 'info'] as const;
const BAR_HEIGHTS = [60, 85, 40, 70, 95, 50, 75, 30];

interface BrandThemePreviewProps {
  resolved: ResolvedTheme;
  name: string;
}

/** The heaviest served weight (heading), or the lightest (body). */
function weightOf(font: ResolvedThemeFont, heaviest: boolean): number | undefined {
  if (!font.weights?.length) return undefined;
  return heaviest ? Math.max(...font.weights) : Math.min(...font.weights);
}

function ModePane({ mode, resolved, name }: BrandThemePreviewProps & { mode: ThemeMode }) {
  const { t } = useCopy();
  const c = resolved.modes[mode] ?? {};
  const categorical = resolved.categorical?.[mode] ?? [];
  const { radius, fonts } = resolved;
  const logoUrl = resolved.assets?.logo ?? null;
  const body = { fontFamily: fonts.body.stack, fontWeight: weightOf(fonts.body, false) };
  const heading = { fontFamily: fonts.heading.stack, fontWeight: weightOf(fonts.heading, true) };
  const mono = { fontFamily: fonts.mono.stack };
  // SASADM-0172 (SASATH-0389, theme engine 6) — non-text top-bar marks; optional, so older
  // documents fall back to appBarInk. Never painted on text.
  const appBarAccent = c.appBarAccent ?? c.appBarInk;
  const modeLabel =
    mode === 'light'
      ? t('brands.theme.preview.light', 'Light')
      : t('brands.theme.preview.dark', 'Dark');

  return (
    <Box
      data-testid={`theme-preview-${mode}`}
      role="group"
      aria-label={t('brands.theme.preview.paneLabel', '{{mode}} preview', { mode: modeLabel })}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
      style={{ backgroundColor: c.bg, color: c.text, ...body }}
    >
      {/* App bar with the brand logo (contract §3 `assets.logo`) */}
      <Box
        data-testid="preview-app-bar"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1 }}
        style={{
          backgroundColor: c.appBar,
          color: c.appBarInk,
          ...(appBarAccent ? { borderBottom: `2px solid ${appBarAccent}` } : {}),
        }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt=""
            sx={{ height: 20, maxWidth: 96, objectFit: 'contain' }}
          />
        ) : null}
        <Box component="span" sx={{ fontSize: 14 }} style={heading}>
          {name}
        </Box>
        {/* The current-tab mark: the label is appBarInk text; only its underline is the accent. */}
        <Box
          component="span"
          sx={{ display: 'inline-flex', flexDirection: 'column', fontSize: 12, ml: 1 }}
        >
          {t('brands.theme.preview.tab', 'Tab')}
          <Box
            component="span"
            aria-hidden="true"
            data-testid="preview-tab-underline"
            sx={{ display: 'block', height: 2, mt: 0.25 }}
            style={{ backgroundColor: appBarAccent }}
          />
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box component="span" sx={{ fontSize: 12 }}>
          {modeLabel}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', minHeight: 320 }}>
        {/* Sidebar */}
        <Box sx={{ width: 96, p: 1, flexShrink: 0 }} style={{ backgroundColor: c.surface2 }}>
          <Box
            sx={{ fontSize: 12, px: 1, py: 0.5, mb: 0.5 }}
            style={{ backgroundColor: c.brandSoft, color: c.text, borderRadius: radius.control }}
          >
            {t('brands.theme.preview.navHome', 'Home')}
          </Box>
          <Box sx={{ fontSize: 12, px: 1, py: 0.5 }} style={{ color: c.text2 }}>
            {t('brands.theme.preview.navReports', 'Reports')}
          </Box>
        </Box>
        {/* Main column */}
        <Box
          sx={{
            flexGrow: 1,
            p: 1.5,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Box
            data-testid="preview-card"
            sx={{ p: 1.5, border: 1 }}
            style={{ backgroundColor: c.surface, borderColor: c.line, borderRadius: radius.card }}
          >
            <Box sx={{ fontSize: 15, mb: 0.5 }} style={{ ...heading, color: c.text }}>
              {t('brands.theme.preview.cardTitle', 'Welcome back')}
            </Box>
            <Box data-testid="preview-text" sx={{ fontSize: 13 }} style={{ color: c.text }}>
              {t('brands.theme.preview.cardBody', 'Your bookings are up to date.')}
            </Box>
            <Box data-testid="preview-text2" sx={{ fontSize: 12 }} style={{ color: c.text2 }}>
              {t('brands.theme.preview.cardSecondary', 'Last synced 2 minutes ago')}
            </Box>
            <Box
              component="span"
              data-testid="preview-link"
              sx={{ fontSize: 12, textDecoration: 'underline' }}
              style={{ color: c.link }}
            >
              {t('brands.theme.preview.link', 'View details')}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              <Box
                component="span"
                data-testid="preview-primary-button"
                sx={{ fontSize: 12, px: 1.5, py: 0.5 }}
                style={{
                  backgroundColor: c.brand,
                  color: c.brandInk,
                  borderRadius: radius.control,
                }}
              >
                {t('brands.theme.preview.primaryButton', 'Primary')}
              </Box>
              <Box
                component="span"
                data-testid="preview-accent-button"
                sx={{ fontSize: 12, px: 1.5, py: 0.5 }}
                style={{
                  backgroundColor: c.accent,
                  color: c.accentInk,
                  borderRadius: radius.control,
                }}
              >
                {t('brands.theme.preview.accentButton', 'Accent')}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Box
                data-testid="preview-input"
                sx={{ flex: 1, fontSize: 12, px: 1, py: 0.5, border: 1 }}
                style={{
                  borderColor: c.lineStrong,
                  borderRadius: radius.control,
                  color: c.text3,
                  backgroundColor: c.surface,
                }}
              >
                {t('brands.theme.preview.inputPlaceholder', 'Search…')}
              </Box>
              <Box
                data-testid="preview-input-focused"
                sx={{ flex: 1, fontSize: 12, px: 1, py: 0.5, border: 1 }}
                style={{
                  borderColor: c.lineStrong,
                  borderRadius: radius.control,
                  outline: `2px solid ${c.focusRing}`,
                  outlineOffset: 1,
                  color: c.text,
                  backgroundColor: c.surface,
                }}
              >
                {t('brands.theme.preview.inputFocused', 'Focused')}
              </Box>
            </Box>
          </Box>
          {/* Status alerts */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0.75 }}>
            {STATUSES.map((s) => (
              <Box
                key={s}
                data-testid={`preview-alert-${s}`}
                sx={{ fontSize: 12, px: 1, py: 0.5, borderLeft: 3 }}
                style={{
                  backgroundColor: c[`${s}Soft`],
                  color: c[`${s}Text`],
                  borderLeftColor: c[`${s}Fg`],
                  borderRadius: radius.control,
                }}
              >
                {t(`brands.theme.preview.alerts.${s}`, s)}
              </Box>
            ))}
          </Box>
          {/* Money */}
          <Box sx={{ display: 'flex', gap: 1.5, fontSize: 13 }} style={mono}>
            <Box
              component="span"
              data-testid="preview-money-positive"
              style={{ color: c.moneyPositive }}
            >
              +1,240.00
            </Box>
            <Box
              component="span"
              data-testid="preview-money-negative"
              sx={{ px: 0.5 }}
              style={{ color: c.moneyNegative, backgroundColor: c.moneyNegativeWash }}
            >
              −86.50
            </Box>
          </Box>
          {/* Categorical chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {categorical.map((cat, i) => (
              <Box
                key={i}
                component="span"
                data-testid={`preview-chip-${i}`}
                sx={{ fontSize: 11, px: 0.75, py: 0.25 }}
                style={{ backgroundColor: cat.fill, color: cat.ink, borderRadius: radius.control }}
              >
                {i + 1}
              </Box>
            ))}
          </Box>
          {/* Small bar chart */}
          <Box
            data-testid="preview-chart"
            role="img"
            aria-label={t('brands.theme.preview.chart', 'Sample bar chart')}
            sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 64, borderBottom: 1 }}
            style={{ borderBottomColor: c.line }}
          >
            {categorical.map((cat, i) => (
              <Box
                key={i}
                sx={{ flex: 1 }}
                style={{
                  height: `${BAR_HEIGHTS[i % BAR_HEIGHTS.length]}%`,
                  backgroundColor: cat.fill,
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// Memoised: the panes re-render only when `resolved` (or the name) changes —
// not on every keystroke in the form, which is a measured cost (~60 styled
// boxes per pane).
export const BrandThemePreview = memo(function BrandThemePreview(props: BrandThemePreviewProps) {
  const { t } = useCopy();
  return (
    <Box>
      <Typography variant="h3" gutterBottom>
        {t('brands.theme.preview.title', 'Live preview')}
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ModePane mode="light" {...props} />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ModePane mode="dark" {...props} />
        </Grid>
      </Grid>
    </Box>
  );
});
